<template>
  <div class="prod-page">
    <ProductionPageHeader
      title="Overview"
      subtitle="Start with missing pieces, then move productions through the pipeline."
    />

    <div v-if="error" class="move-alert-error">{{ error }}</div>
    <div v-else-if="loading" class="flex justify-center py-16">
      <div class="move-spinner" aria-label="Loading overview" />
    </div>
    <template v-else>
      <section class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-5">
        <div class="move-stat-card move-stat-card--primary">
          <p class="move-stat-card__label">Projects</p>
          <p class="move-stat-card__value">{{ stats.projects || 0 }}</p>
          <p class="text-xs mt-1" style="color: inherit; opacity: 0.75">{{ stats.active_projects || 0 }} active</p>
        </div>
        <div class="move-stat-card move-stat-card--secondary">
          <p class="move-stat-card__label">Productions</p>
          <p class="move-stat-card__value">{{ stats.productions || 0 }}</p>
        </div>
        <div class="move-stat-card move-stat-card--tertiary">
          <p class="move-stat-card__label">Missing items</p>
          <p class="move-stat-card__value">{{ stats.open_gaps || 0 }}</p>
          <p class="text-xs mt-1" style="color: inherit; opacity: 0.75">
            {{ stats.productions_with_gaps || 0 }} {{ (stats.productions_with_gaps || 0) === 1 ? 'production' : 'productions' }}
          </p>
        </div>
        <div class="move-stat-card move-stat-card--secondary">
          <p class="move-stat-card__label">In editing</p>
          <p class="move-stat-card__value">{{ byStatus.editing || 0 }}</p>
        </div>
      </section>

      <section class="prod-pipeline">
        <router-link
          v-for="step in productionProgressSteps"
          :key="step.value"
          :to="{ path: '/production/productions', query: { status: step.value } }"
          class="prod-pipeline__item"
        >
          <span class="prod-pipeline__count">{{ byStatus[step.value] || 0 }}</span>
          <span class="prod-pipeline__label">{{ step.label }}</span>
        </router-link>
      </section>

      <ProductionAttentionList
        :items="stats.needs_attention || []"
        :empty-icon="(stats.productions || 0) === 0 ? 'theaters' : 'check_circle'"
        :empty-title="(stats.productions || 0) === 0 ? 'No productions yet' : 'Nothing blocking a shoot'"
        :empty-body="(stats.productions || 0) === 0
          ? 'Create a production under a project to track talent, scenes, and shoot days.'
          : 'Every production has the people and pieces it needs. Open productions to keep going.'"
      />

      <section class="move-card overflow-hidden !p-0">
        <div class="flex items-center justify-between px-5 py-4 border-b" style="border-color: var(--move-border)">
          <h3 class="text-sm font-bold move-text">Upcoming shoots</h3>
          <router-link to="/production/productions" class="text-sm font-semibold" style="color: var(--move-accent)">View all</router-link>
        </div>
        <div v-if="!(stats.upcoming_shoots || []).length" class="move-empty-state py-12">
          <span class="material-icons">event</span>
          <p class="font-semibold move-text">No upcoming shoot days</p>
          <p class="text-sm move-text-faint mt-1">Schedule a shoot on a production to see it here.</p>
        </div>
        <ul v-else class="divide-y" style="border-color: var(--move-border)">
          <li v-for="item in stats.upcoming_shoots" :key="item.id">
            <router-link :to="`/production/productions/${item.id}/shoot`" class="flex items-center justify-between gap-3 px-5 py-3.5 hover:bg-[var(--move-accent-soft)]">
              <div class="min-w-0">
                <p class="font-semibold move-text truncate">{{ item.name }}</p>
                <p class="text-xs move-text-faint">{{ item.project?.name }} · {{ item.shoot_venue || 'Venue TBC' }}</p>
              </div>
              <span class="text-sm move-text-muted shrink-0">{{ formatDateTime(item.shoot_at) }}</span>
            </router-link>
          </li>
        </ul>
      </section>
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import ProductionPageHeader from '../../components/production/ProductionPageHeader.vue'
import ProductionAttentionList from '../../components/production/ProductionAttentionList.vue'
import { productionApi } from '../../services/productionApi'
import { productionProgressSteps } from '../../config/productionNavigation'
import { formatDateTime } from '../../utils/productionDates'

const loading = ref(true)
const error = ref('')
const stats = ref({})

const byStatus = computed(() => stats.value.productions_by_status || {})

onMounted(async () => {
  try {
    const body = await productionApi.dashboard()
    stats.value = body.data || {}
  } catch (e) {
    error.value = e.response?.data?.message || 'Failed to load overview.'
  } finally {
    loading.value = false
  }
})
</script>
